import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from '@mui/material/Alert';

import EMIsAPI from "../../services/emi-api";
import MainHeader from "../../components/Header/MainHeader";
import CustomButton from "../../components/Button/CustomButton";

import {
    handleShowContextualLoader,
    handlehideContextualLoader,
} from "../../utils/contextualLoaderTrigger";

import AccountDetails from "./AccountDetails";
import AccountNotFound from "./AccountNotFound";

import styles from "./styles.module.scss";
import { getIsLentraCustomer } from "../../utils/storage";
import { IS_VIRTUAL_ACCOUNT_NUMBER_ENABLE } from "../../utils/environment";

const ACCOUNT_LABELS = {
    account_number: "Account Number",
    bankName: "Bank Name",
    ifsc: "IFSC Code",
    recName: "Beneficiary Name",
    accountValidity: "Account Validity",
};

const RepaymentBankAccountDetail = () => {
    const [accountDetail, setAccountDetail] = useState({});
    const [accountKeys, setAccountKeys] = useState([]);
    const [status, setStatus] = useState('inactive');

    const apiRef = useRef(new EMIsAPI());
    const inProgressRequest = useRef(false);
    const navigate = useNavigate();

    const {
        generateVirtualAccount,
        fetchVirtualAccount,
    } = apiRef.current;

    const showToast = ({
        message,
        type = "success",
        position = "bottom",
        timer = 2000
    }) => {
        Swal.fire({
            toast: true,
            position,
            icon: type,
            title: message,
            showConfirmButton: false,
            timer,
            timerProgressBar: true,
        });
    };

    const handleApiResponse = useMemo(() => (response) => {
        const {
            data: { details, status } = {},
            message,
            statusCode
        } = response;
        setAccountDetail(details ?? {});
        setStatus(status);
        setAccountKeys(Object.keys(details ?? {}));

        setTimeout(() => {
            handlehideContextualLoader();
            showToast({
                message,
                type: statusCode === 200 ? "success" : "error",
            });
        }, 1000);
    }, []);

    const executeRequest = async (requestFn) => {
        if (inProgressRequest.current) return null;

        try {
            handleShowContextualLoader();
            inProgressRequest.current = true;

            return await requestFn();
        } catch (err) {
            handlehideContextualLoader();
            showToast({
                message: err.message || 'something went wrong',
                type: 'error',
                timer: 2000
            });
        }
        finally {
            inProgressRequest.current = false;
        }
    };

    const fetchAccount = useMemo(() => async () => {
        const response = await executeRequest(
            fetchVirtualAccount
        );

        if (!response) return;

        handleApiResponse(response);

    }, [fetchVirtualAccount, handleApiResponse]);

    const generateAccount = useMemo(() => async () => {
        const response = await executeRequest(
            generateVirtualAccount
        );

        if (!response) return;

        handleApiResponse(response);

    }, [generateVirtualAccount, handleApiResponse]);

    const copyText = async (text = "") => {
        try {
            await navigator.clipboard.writeText(text);

            showToast({
                message: "Copied to clipboard",
            });
        } catch (error) {
            showToast({
                message:
                    error?.message ||
                    "Something went wrong",
                type: "error",
            });
        }
    };

    useEffect(() => {
        fetchAccount();
        return () => {
            Swal.close();
        }
    }, [fetchAccount]);

    const isAccountDetailAvailable =
        accountKeys.length > 0;

    const showButton =
        !isAccountDetailAvailable ||
        status?.toLowerCase() ===
        "inactive";

    const buttonText = isAccountDetailAvailable
        ? "Regenerate Virtual Account Number"
        : "Generate Virtual Account Number";

    const isLentraCustomer = getIsLentraCustomer()

    if(!IS_VIRTUAL_ACCOUNT_NUMBER_ENABLE || isLentraCustomer === 'true') {
        navigate('/');
        return null;
    }

    return (
        <div className="wrapper auto-height">
            <MainHeader 
                title="Bank Account Detail for Repayment"
            />

            <div
                className={`flex-1 ${styles.accountDetailWrapper}`}
            >
                {!isAccountDetailAvailable && (
                    <AccountNotFound />
                )}

                <AccountDetails
                    accountKeys={accountKeys}
                    accountDetail={accountDetail}
                    onCopy={copyText}
                    lables={ACCOUNT_LABELS}
                />
                {isAccountDetailAvailable && <Alert severity="info" sx={{
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.info.main}`,
                }}>
                    Payments through mobile banking and net banking are supported
                </Alert>}
            </div>

            {showButton && (
                <div className="bottom-button-fixed">
                    <CustomButton
                        text={buttonText}
                        onClick={generateAccount}
                    />
                </div>
            )}
        </div>
    );
};

export default RepaymentBankAccountDetail;